import { Fragment, useEffect, useMemo, useState } from "react";
import {
  cancelPaymentBatchApi,
  createPaymentBatch,
  deletePaymentBatchApi,
  fetchAccounts,
  fetchCompanies,
  fetchOpenSupplierInvoicesForPayment,
  fetchPaymentBatches,
  paymentBatchXmlUrl,
  postPaymentBatch,
  type Account,
  type Company,
  type PaymentBatch,
  type SupplierOpenInvoiceRow,
} from "../api";

/** Etiqueta que representa a las facturas sin método de pago (PeyMethod
 *  NULL o vacío). Se usa como clave en el set de filtros y en los chips. */
const SIN_METODO = "(sin método)";

/** Columnas de la tabla de facturas abiertas con su ancho default. El
 *  ancho se puede editar arrastrando el borde derecho del <th> y se
 *  persiste por columna en localStorage. */
type ColId =
  | "check"
  | "provider"
  | "docNum"
  | "ref"
  | "docDate"
  | "dueDate"
  | "payMethod"
  | "total"
  | "pending"
  | "toPay"
  | "concept";

type ColDef = { id: ColId; label: string; defaultWidth: number; alignRight?: boolean };

const INVOICE_COLUMNS: ColDef[] = [
  { id: "check", label: "", defaultWidth: 32 },
  { id: "provider", label: "Proveedor", defaultWidth: 280 },
  { id: "docNum", label: "Nº fra SAP", defaultWidth: 90 },
  { id: "ref", label: "Ref. proveedor", defaultWidth: 120 },
  { id: "docDate", label: "F. factura", defaultWidth: 100 },
  { id: "dueDate", label: "Vencimiento", defaultWidth: 110 },
  { id: "payMethod", label: "Método pago", defaultWidth: 140 },
  { id: "total", label: "Total", defaultWidth: 100, alignRight: true },
  { id: "pending", label: "Pendiente", defaultWidth: 110, alignRight: true },
  { id: "toPay", label: "A pagar", defaultWidth: 110, alignRight: true },
  { id: "concept", label: "Concepto SEPA", defaultWidth: 200 },
];

const INVOICE_COLS_STORAGE_KEY = "paymentBatches.invoiceColumnWidths.v1";

function loadColumnWidths(): Record<ColId, number> {
  const defaults: Record<string, number> = Object.fromEntries(
    INVOICE_COLUMNS.map((c) => [c.id, c.defaultWidth]),
  );
  try {
    const raw = localStorage.getItem(INVOICE_COLS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === "number" && v >= 32 && v <= 800) defaults[k] = v;
      }
    }
  } catch {
    /* localStorage bloqueado o valor corrupto — usa defaults */
  }
  return defaults as Record<ColId, number>;
}

/**
 * <th> con un handle draggable en el borde derecho para redimensionar la
 * columna. `onResize` recibe el nuevo ancho en px cada vez que el ratón
 * se mueve durante el arrastre. Se usa dentro de una tabla con <colgroup>.
 */
function ResizableTh({
  width,
  onResize,
  align,
  children,
}: {
  width: number;
  onResize: (newWidth: number) => void;
  align?: "left" | "right";
  children: React.ReactNode;
}): JSX.Element {
  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(40, Math.min(800, startWidth + (ev.clientX - startX)));
      onResize(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  return (
    <th style={{ position: "relative", textAlign: align === "right" ? "right" : "left" }}>
      {children}
      <div
        onMouseDown={onMouseDown}
        title="Arrastra para cambiar el ancho de la columna"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: 6,
          cursor: "col-resize",
          userSelect: "none",
        }}
      />
    </th>
  );
}

/**
 * Pagos a proveedor: seleccionas empresa + cuenta emisora, marcas facturas
 * abiertas y generas fichero SEPA (pain.001.001.03) para enviar al banco.
 * Cada fichero generado queda en el historial y desde ahí lo contabilizas
 * en SAP (un VendorPayment por factura, para que cada apunte del banco
 * cuadre 1:1 con el cargo).
 */
export function PaymentBatches(): JSX.Element {
  // Activa layout ancho (main sin max-width 1400) mientras la vista está
  // montada. La tabla tiene muchas columnas y sin esto se queda estrecha.
  useEffect(() => {
    document.body.classList.add("main-wide");
    return () => document.body.classList.remove("main-wide");
  }, []);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [selectedIban, setSelectedIban] = useState<string>("");
  const [invoices, setInvoices] = useState<SupplierOpenInvoiceRow[] | null>(null);
  const [batches, setBatches] = useState<PaymentBatch[]>([]);
  const [selection, setSelection] = useState<Map<number, { amount: number; remittance: string }>>(new Map());
  const [execDate, setExecDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Set de métodos de pago (OPCH.PeyMethod) actualmente ACTIVOS. Solo las
   *  filas cuyo método esté en este set se muestran en la tabla. Se
   *  inicializa al cargar facturas: todos los métodos activos EXCEPTO los
   *  que parecen domiciliación (heurística `/domicil/i`). El usuario puede
   *  reactivar cualquiera con un click en el chip correspondiente. */
  const [activePayMethods, setActivePayMethods] = useState<Set<string>>(new Set());

  /** Ancho de cada columna (px), persistido en localStorage entre sesiones
   *  con clave INVOICE_COLS_STORAGE_KEY. Al cambiarlo por arrastre, se
   *  guarda automáticamente en el efecto de abajo. */
  const [columnWidths, setColumnWidths] = useState<Record<ColId, number>>(() => loadColumnWidths());
  useEffect(() => {
    try {
      localStorage.setItem(INVOICE_COLS_STORAGE_KEY, JSON.stringify(columnWidths));
    } catch {
      /* ignora fallos de storage (cuota, modo privado, ...) */
    }
  }, [columnWidths]);
  const setColWidth = (id: ColId, w: number) =>
    setColumnWidths((prev) => ({ ...prev, [id]: w }));
  const resetColWidths = () =>
    setColumnWidths(
      Object.fromEntries(INVOICE_COLUMNS.map((c) => [c.id, c.defaultWidth])) as Record<ColId, number>,
    );

  // Carga inicial: empresas con SAP + todas las cuentas.
  useEffect(() => {
    (async () => {
      try {
        const [c, a] = await Promise.all([fetchCompanies(), fetchAccounts()]);
        const withSap = c.items.filter((x) => x.sap);
        setCompanies(withSap);
        setAccounts(a.items);
        if (withSap.length > 0 && !selectedCompany) setSelectedCompany(withSap[0]!.id);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuentas de la empresa seleccionada que tienen G/L SAP (imprescindible
  // para contabilizar el pago). Si el usuario elige una cuenta sin G/L, el
  // backend rechazará; aquí las mostramos como deshabilitadas y avisamos.
  const companyAccounts = useMemo(
    () => accounts.filter((a) => a.companyId === selectedCompany && a.active),
    [accounts, selectedCompany],
  );

  // Al cambiar de empresa, reseteamos el resto.
  useEffect(() => {
    setInvoices(null);
    setSelection(new Map());
    setSelectedIban("");
    setError(null);
    setNotice(null);
    if (selectedCompany) void reloadBatches();
    else setBatches([]);
    // Preseleccionar la primera cuenta con G/L SAP configurado.
    const firstWithGL = companyAccounts.find((a) => a.sapGLAccount);
    if (firstWithGL) setSelectedIban(firstWithGL.iban);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const reloadBatches = async () => {
    if (!selectedCompany) return;
    try {
      const r = await fetchPaymentBatches(selectedCompany);
      setBatches(r.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const loadInvoices = async () => {
    if (!selectedCompany) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await fetchOpenSupplierInvoicesForPayment(selectedCompany);
      setInvoices(r.items);
      setSelection(new Map());
      // Inicializar el filtro de métodos: activar todos EXCEPTO los que
      // parecen domiciliación (cualquier variante ortográfica del código).
      const initial = new Set<string>();
      for (const inv of r.items) {
        const m = inv.PayMethod || SIN_METODO;
        if (!/domicil/i.test(m)) initial.add(m);
      }
      setActivePayMethods(initial);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** Cuenta las facturas por método de pago sobre el resultado bruto (antes
   *  de aplicar el filtro), para pintar los chips con contador. */
  const payMethodStats = useMemo(() => {
    if (!invoices) return [] as Array<{ method: string; count: number }>;
    const counts = new Map<string, number>();
    for (const r of invoices) {
      const m = r.PayMethod || SIN_METODO;
      counts.set(m, (counts.get(m) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0], "es"))
      .map(([method, count]) => ({ method, count }));
  }, [invoices]);

  /** Facturas visibles tras aplicar el filtro por método de pago. */
  const visibleInvoices = useMemo(() => {
    if (!invoices) return null;
    return invoices.filter((r) => activePayMethods.has(r.PayMethod || SIN_METODO));
  }, [invoices, activePayMethods]);

  const togglePayMethod = (method: string) => {
    const next = new Set(activePayMethods);
    if (next.has(method)) {
      next.delete(method);
      // Al ocultar un método, sacar de `selection` las facturas de ese
      // método — si no, el batch podría llevar transferencias que el
      // usuario ya no ve en pantalla (fuente de sorpresas).
      if (invoices) {
        const nextSel = new Map(selection);
        for (const r of invoices) {
          if ((r.PayMethod || SIN_METODO) === method) {
            nextSel.delete(r.DocEntry);
          }
        }
        setSelection(nextSel);
      }
    } else {
      next.add(method);
    }
    setActivePayMethods(next);
  };

  const setAllPayMethodsActive = (active: boolean) => {
    if (active) {
      setActivePayMethods(new Set(payMethodStats.map((m) => m.method)));
    } else {
      setActivePayMethods(new Set());
      setSelection(new Map());
    }
  };

  const toggleInvoice = (row: SupplierOpenInvoiceRow) => {
    if (!row.BpIban) return; // no se puede pagar sin IBAN
    const next = new Map(selection);
    if (next.has(row.DocEntry)) {
      next.delete(row.DocEntry);
    } else {
      next.set(row.DocEntry, {
        amount: row.PendingAmount,
        remittance: `Fra ${row.NumAtCard || row.DocNum}`,
      });
    }
    setSelection(next);
  };

  const updateSelection = (docEntry: number, patch: Partial<{ amount: number; remittance: string }>) => {
    const cur = selection.get(docEntry);
    if (!cur) return;
    const next = new Map(selection);
    next.set(docEntry, { ...cur, ...patch });
    setSelection(next);
  };

  const toggleSelectAllPayable = () => {
    if (!visibleInvoices) return;
    const payable = visibleInvoices.filter((r) => r.BpIban);
    if (payable.length > 0 && payable.every((r) => selection.has(r.DocEntry))) {
      // Deseleccionar solo las visibles pagables (respeta cualquier
      // selección de facturas ocultas).
      const next = new Map(selection);
      for (const r of payable) next.delete(r.DocEntry);
      setSelection(next);
    } else {
      const next = new Map(selection);
      for (const r of payable) {
        if (next.has(r.DocEntry)) continue;
        next.set(r.DocEntry, {
          amount: r.PendingAmount,
          remittance: `Fra ${r.NumAtCard || r.DocNum}`,
        });
      }
      setSelection(next);
    }
  };

  const totalSelected = useMemo(() => {
    let t = 0;
    for (const [, v] of selection) t += Number(v.amount) || 0;
    return Math.round(t * 100) / 100;
  }, [selection]);

  const generateBatch = async () => {
    if (!selectedCompany || !selectedIban || selection.size === 0) return;
    const account = companyAccounts.find((a) => a.iban === selectedIban);
    if (!account?.sapGLAccount) {
      setError(`La cuenta ${selectedIban} no tiene cuenta contable SAP (G/L). Rellénala en Cuentas antes de generar.`);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const lines = Array.from(selection.entries()).map(([docEntry, v]) => ({
        invoiceDocEntry: docEntry,
        amount: v.amount,
        remittanceInfo: v.remittance,
      }));
      const r = await createPaymentBatch({
        companyId: selectedCompany,
        senderIban: selectedIban,
        requestedExecutionDate: execDate,
        lines,
      });
      setNotice(
        `✓ Fichero generado: ${r.batch.filename} · ${r.batch.lines.length} transferencias · total ${eur(r.batch.totalAmount)}`,
      );
      // Descarga automática del XML.
      window.open(paymentBatchXmlUrl(r.batch.id), "_blank");
      // Refresca la tabla (los pendientes bajan) y la lista de batches.
      await Promise.all([loadInvoices(), reloadBatches()]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const groupedByCard = useMemo(() => {
    if (!visibleInvoices) return [] as Array<{ cardCode: string; cardName: string; bpIban: string; rows: SupplierOpenInvoiceRow[] }>;
    const map = new Map<string, { cardCode: string; cardName: string; bpIban: string; rows: SupplierOpenInvoiceRow[] }>();
    for (const r of visibleInvoices) {
      const cur = map.get(r.CardCode);
      if (cur) cur.rows.push(r);
      else map.set(r.CardCode, { cardCode: r.CardCode, cardName: r.CardName, bpIban: r.BpIban, rows: [r] });
    }
    // Orden: primero los que tienen IBAN, luego alfabético.
    return Array.from(map.values()).sort((a, b) => {
      if (!a.bpIban && b.bpIban) return 1;
      if (a.bpIban && !b.bpIban) return -1;
      return a.cardName.localeCompare(b.cardName, "es");
    });
  }, [visibleInvoices]);

  return (
    <>
      <section className="card">
        <div className="card-head">
          <div>
            <h2>Pagos a proveedor</h2>
            <p className="meta">
              Selecciona empresa y cuenta emisora, marca las facturas a pagar y genera el
              fichero SEPA (pain.001.001.03) para enviar al banco. Cada transferencia irá
              como cargo independiente en el extracto (BtchBookg=false). Usa los chips de
              método de pago para excluir domiciliaciones u otros métodos que no vayan por
              transferencia.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label>
              Empresa:{" "}
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={busy || companies.length === 0}
              >
                {companies.length === 0 && <option>Sin empresas SAP</option>}
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.alias || c.name}</option>
                ))}
              </select>
            </label>
            <label>
              Cuenta emisora:{" "}
              <select
                value={selectedIban}
                onChange={(e) => setSelectedIban(e.target.value)}
                disabled={busy || companyAccounts.length === 0}
              >
                <option value="">— selecciona —</option>
                {companyAccounts.map((a) => (
                  <option key={a.iban} value={a.iban} disabled={!a.sapGLAccount}>
                    {a.alias || a.iban} ({a.iban})
                    {a.sapGLAccount ? ` · G/L ${a.sapGLAccount}` : " · SIN G/L SAP"}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Fecha ejecución:{" "}
              <input type="date" value={execDate} onChange={(e) => setExecDate(e.target.value)} disabled={busy} />
            </label>
            <button type="button" onClick={loadInvoices} disabled={busy || !selectedCompany}>
              {busy ? "Cargando…" : "↻ Cargar facturas abiertas"}
            </button>
          </div>
        </div>

        {error && <p className="error">{error}</p>}
        {notice && (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              background: "rgba(80,200,120,0.08)",
              padding: 8,
              borderRadius: 4,
              margin: "8px 0",
            }}
          >
            {notice}
          </pre>
        )}

        {invoices !== null && invoices.length === 0 && (
          <p className="meta">✓ No hay facturas de proveedor abiertas para esta empresa.</p>
        )}

        {invoices && invoices.length > 0 && (
          <>
            {payMethodStats.length > 1 && (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  margin: "12px 0 4px 0",
                  flexWrap: "wrap",
                }}
              >
                <span className="meta" style={{ marginRight: 4 }}>Método pago:</span>
                {payMethodStats.map(({ method, count }) => {
                  const active = activePayMethods.has(method);
                  return (
                    <button
                      key={method}
                      type="button"
                      onClick={() => togglePayMethod(method)}
                      disabled={busy}
                      title={active ? "Click para OCULTAR estas facturas" : "Click para MOSTRAR estas facturas"}
                      style={{
                        padding: "2px 10px",
                        borderRadius: 12,
                        fontSize: "0.85em",
                        border: `1px solid ${active ? "#4a8" : "#ccc"}`,
                        background: active ? "rgba(80,180,120,0.15)" : "rgba(150,150,150,0.08)",
                        color: active ? "#282" : "#888",
                        cursor: "pointer",
                        textDecoration: active ? "none" : "line-through",
                      }}
                    >
                      {method} ({count})
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setAllPayMethodsActive(true)}
                  disabled={busy}
                  style={{ fontSize: "0.8em", padding: "2px 8px" }}
                >
                  Todos
                </button>
                <button
                  type="button"
                  onClick={() => setAllPayMethodsActive(false)}
                  disabled={busy}
                  style={{ fontSize: "0.8em", padding: "2px 8px" }}
                >
                  Ninguno
                </button>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "12px 0", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={toggleSelectAllPayable}
                disabled={busy || !visibleInvoices || visibleInvoices.length === 0}
              >
                {selection.size > 0 ? "Deseleccionar" : "Seleccionar todas (con IBAN)"}
              </button>
              <span className="meta">
                {selection.size} seleccionada(s) · Total{" "}
                <strong>{eur(totalSelected)}</strong>
                {visibleInvoices && invoices && visibleInvoices.length !== invoices.length && (
                  <> · Mostrando {visibleInvoices.length} de {invoices.length} facturas</>
                )}
              </span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  onClick={resetColWidths}
                  disabled={busy}
                  title="Restaurar el ancho por defecto de todas las columnas"
                  style={{ fontSize: "0.85em" }}
                >
                  ↔ Restaurar anchos
                </button>
                <button
                  type="button"
                  onClick={generateBatch}
                  disabled={
                    busy ||
                    selection.size === 0 ||
                    !selectedIban ||
                    !companyAccounts.find((a) => a.iban === selectedIban)?.sapGLAccount
                  }
                >
                  📄 Generar fichero SEPA ({selection.size})
                </button>
              </div>
            </div>

            {visibleInvoices && visibleInvoices.length === 0 && (
              <p className="meta" style={{ margin: "12px 0", padding: 8, background: "rgba(255,180,50,0.10)", borderRadius: 4 }}>
                No hay facturas visibles con los métodos de pago actualmente activos.
                Activa algún chip de arriba para verlas.
              </p>
            )}

            <div style={{ overflowX: "auto", width: "100%" }}>
              <table
                className="data compact"
                style={{
                  whiteSpace: "nowrap",
                  width: "100%",
                  tableLayout: "fixed",
                }}
              >
                <colgroup>
                  {INVOICE_COLUMNS.map((col) => (
                    <col key={col.id} style={{ width: `${columnWidths[col.id]}px` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {INVOICE_COLUMNS.map((col) => (
                      <ResizableTh
                        key={col.id}
                        width={columnWidths[col.id]}
                        onResize={(w) => setColWidth(col.id, w)}
                        align={col.alignRight ? "right" : "left"}
                      >
                        {col.label}
                      </ResizableTh>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupedByCard.map((g) =>
                    g.rows.map((r, idx) => {
                      const sel = selection.get(r.DocEntry);
                      const hasIban = Boolean(r.BpIban);
                      const isFirst = idx === 0;
                      // Cada celda con overflow:hidden + ellipsis para que
                      // si el usuario estrecha la columna, el contenido se
                      // recorte con "…" en lugar de romper el layout.
                      const cellStyle: React.CSSProperties = {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      };
                      // Tooltip nativo con el IBAN si el proveedor lo tiene
                      // (aparece al pasar el ratón por cualquier celda de la fila).
                      const rowTitle = hasIban
                        ? `IBAN acreedor: ${formatIban(r.BpIban)}`
                        : "El proveedor NO tiene IBAN configurado en SAP. Rellénalo en la ficha del BP (OCRD.DflIBAN) para poder incluirlo en un fichero SEPA.";
                      return (
                        <tr
                          key={r.DocEntry}
                          title={rowTitle}
                          style={{
                            opacity: hasIban ? 1 : 0.6,
                            borderTop: isFirst ? "1px solid rgba(120,150,200,0.25)" : undefined,
                          }}
                        >
                          <td style={cellStyle}>
                            <input
                              type="checkbox"
                              checked={Boolean(sel)}
                              disabled={!hasIban || busy}
                              onChange={() => toggleInvoice(r)}
                            />
                          </td>
                          <td style={cellStyle}>
                            {isFirst ? (
                              <>
                                <span style={{ fontWeight: 600 }}>{r.CardName}</span>{" "}
                                <span style={{ fontSize: "0.85em", color: "#888" }}>({r.CardCode})</span>
                                {!hasIban && (
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      color: "#c33",
                                      fontSize: "0.85em",
                                      fontWeight: 600,
                                    }}
                                  >
                                    ⚠ Sin IBAN configurada
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                <span style={{ color: "#aaa" }}>↳ misma</span>
                                {!hasIban && (
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      color: "#c33",
                                      fontSize: "0.85em",
                                      fontWeight: 600,
                                    }}
                                  >
                                    ⚠ Sin IBAN
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td style={cellStyle}>{r.DocNum}</td>
                          <td style={cellStyle}>{r.NumAtCard || <span style={{ color: "#aaa" }}>—</span>}</td>
                          <td style={cellStyle}>{r.DocDate}</td>
                          <td style={cellStyle}>{r.DocDueDate}</td>
                          <td style={{ ...cellStyle, fontSize: "0.85em", color: "#666" }}>
                            {r.PayMethod || <span style={{ color: "#aaa" }}>—</span>}
                          </td>
                          <td style={{ ...cellStyle, textAlign: "right" }}>{eur(r.DocTotal)}</td>
                          <td style={{ ...cellStyle, textAlign: "right" }}>
                            <strong>{eur(r.PendingAmount)}</strong>
                          </td>
                          <td style={{ ...cellStyle, textAlign: "right" }}>
                            {sel ? (
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                max={r.PendingAmount}
                                value={sel.amount}
                                onChange={(e) =>
                                  updateSelection(r.DocEntry, { amount: Number(e.target.value) })
                                }
                                style={{ width: "95%", textAlign: "right" }}
                              />
                            ) : (
                              <span style={{ color: "#aaa" }}>—</span>
                            )}
                          </td>
                          <td style={cellStyle}>
                            {sel ? (
                              <input
                                type="text"
                                value={sel.remittance}
                                maxLength={140}
                                onChange={(e) =>
                                  updateSelection(r.DocEntry, { remittance: e.target.value })
                                }
                                style={{ width: "95%" }}
                              />
                            ) : (
                              <span style={{ color: "#aaa" }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    }),
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <BatchesHistory
        batches={batches}
        onChange={reloadBatches}
      />
    </>
  );
}

function BatchesHistory({
  batches,
  onChange,
}: {
  batches: PaymentBatch[];
  onChange: () => Promise<void>;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const doPost = async (id: string) => {
    if (!confirm("¿Contabilizar en SAP todas las líneas pendientes de este fichero?")) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await postPaymentBatch(id);
      const fails = r.results
        .filter((x) => !x.ok)
        .map((x) => `  • Fra ${x.invoiceDocNum}: ${x.error}`)
        .join("\n");
      setMsg(
        `Contabilización: ${r.okCount} OK, ${r.failCount} fallo(s).` +
          (fails ? `\nErrores:\n${fails}` : ""),
      );
      await onChange();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async (id: string) => {
    if (!confirm("¿Anular este fichero? No se podrá reactivar. (Solo se puede anular si no hay líneas ya contabilizadas.)")) return;
    setBusy(true);
    setErr(null);
    try {
      await cancelPaymentBatchApi(id);
      await onChange();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (id: string) => {
    if (!confirm("¿Borrar definitivamente este fichero del histórico?")) return;
    setBusy(true);
    setErr(null);
    try {
      await deletePaymentBatchApi(id);
      await onChange();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <div>
          <h2>Ficheros generados</h2>
          <p className="meta">
            Histórico de ficheros SEPA. Descarga el XML para (re)enviarlo al banco o
            contabilízalo en SAP cuando el cargo esté hecho.
          </p>
        </div>
      </div>
      {err && <p className="error">{err}</p>}
      {msg && (
        <pre
          style={{
            whiteSpace: "pre-wrap",
            background: "rgba(80,160,255,0.06)",
            padding: 8,
            borderRadius: 4,
          }}
        >
          {msg}
        </pre>
      )}
      {batches.length === 0 && <p className="meta">Aún no hay ficheros generados.</p>}
      {batches.length > 0 && (
        <div style={{ overflowX: "auto", width: "100%" }}>
          <table className="data" style={{ whiteSpace: "nowrap", width: "100%" }}>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Fichero</th>
              <th>Cuenta emisora</th>
              <th>Ejec.</th>
              <th style={{ textAlign: "right" }}>Total</th>
              <th style={{ textAlign: "right" }}>Transf.</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => {
              const posted = b.lines.filter((l) => l.status === "posted").length;
              const failed = b.lines.filter((l) => l.status === "failed").length;
              const pending = b.lines.filter((l) => l.status === "pending").length;
              const canPost = b.status !== "cancelled" && (pending > 0 || failed > 0);
              const canCancel = b.status === "generated" && posted === 0;
              const canDelete = posted === 0;
              return (
                <Fragment key={b.id}>
                  <tr>
                    <td>{b.createdAt.slice(0, 16).replace("T", " ")}</td>
                    <td style={{ fontFamily: "monospace", fontSize: "0.85em" }}>{b.filename}</td>
                    <td>
                      {b.senderAccountAlias}{" "}
                      <span style={{ fontSize: "0.8em", color: "#888" }}>({formatIban(b.senderIban)})</span>
                    </td>
                    <td>{b.requestedExecutionDate}</td>
                    <td style={{ textAlign: "right" }}>{eur(b.totalAmount)}</td>
                    <td style={{ textAlign: "right" }}>
                      {b.lines.length} <span style={{ color: "#888" }}>({posted}✓ / {failed}✗ / {pending}⏳)</span>
                    </td>
                    <td>{statusBadge(b.status)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <a href={paymentBatchXmlUrl(b.id)} target="_blank" rel="noreferrer">
                        <button type="button" disabled={busy}>⬇ XML</button>
                      </a>{" "}
                      <button
                        type="button"
                        onClick={() => setExpandedId(expandedId === b.id ? null : b.id)}
                        disabled={busy}
                      >
                        {expandedId === b.id ? "▲ Cerrar" : "▼ Detalle"}
                      </button>{" "}
                      {canPost && (
                        <button type="button" onClick={() => doPost(b.id)} disabled={busy}>
                          Contabilizar en SAP
                        </button>
                      )}{" "}
                      {canCancel && (
                        <button type="button" onClick={() => doCancel(b.id)} disabled={busy}>
                          Anular
                        </button>
                      )}{" "}
                      {canDelete && (
                        <button
                          type="button"
                          className="danger"
                          onClick={() => doDelete(b.id)}
                          disabled={busy}
                        >
                          Borrar
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedId === b.id && (
                    <tr>
                      <td colSpan={8} style={{ background: "rgba(120,150,200,0.05)" }}>
                        <BatchDetail batch={b} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function BatchDetail({ batch }: { batch: PaymentBatch }) {
  return (
    <div style={{ padding: 12 }}>
      <p className="meta" style={{ margin: "0 0 8px 0" }}>
        MsgId SEPA <code>{batch.sepaMessageId}</code> · Generado por {batch.createdBy}
        {" · "}Actualizado {batch.updatedAt.slice(0, 16).replace("T", " ")}
      </p>
      <div style={{ overflowX: "auto", width: "100%" }}>
      <table
        className="data compact"
        style={{ whiteSpace: "nowrap", width: "100%" }}
      >
        <thead>
          <tr>
            <th>Proveedor</th>
            <th>Nº fra SAP</th>
            <th>Ref. proveedor</th>
            <th>Vencimiento</th>
            <th style={{ textAlign: "right" }}>Importe</th>
            <th>Concepto</th>
            <th>IBAN acreedor</th>
            <th>Estado SAP</th>
          </tr>
        </thead>
        <tbody>
          {batch.lines.map((l) => (
            <tr key={l.id}>
              <td>
                {l.cardName}{" "}
                <span style={{ fontSize: "0.8em", color: "#888" }}>({l.cardCode})</span>
              </td>
              <td>{l.invoiceDocNum}</td>
              <td>{l.invoiceNumAtCard || "—"}</td>
              <td>{l.invoiceDueDate}</td>
              <td style={{ textAlign: "right" }}>{eur(l.amount)}</td>
              <td>{l.remittanceInfo}</td>
              <td style={{ fontFamily: "monospace", fontSize: "0.85em" }}>{formatIban(l.creditorIban)}</td>
              <td>
                {l.status === "posted" && (
                  <span style={{ color: "#3a3" }}>
                    ✓ DocNum {l.sapDocNum ?? "?"} · JdtNum {l.sapJdtNum ?? "?"}
                  </span>
                )}
                {l.status === "failed" && (
                  <span style={{ color: "#c33" }} title={l.lastError ?? ""}>
                    ✗ Error
                  </span>
                )}
                {l.status === "pending" && <span style={{ color: "#888" }}>⏳ Pendiente</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {batch.lines.some((l) => l.status === "failed") && (
        <div style={{ marginTop: 8, fontSize: "0.9em" }}>
          <strong>Errores:</strong>
          <ul style={{ margin: "4px 0 0 20px" }}>
            {batch.lines
              .filter((l) => l.status === "failed")
              .map((l) => (
                <li key={l.id}>
                  Fra {l.invoiceDocNum} · {l.cardName}: {l.lastError}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function eur(n: number): string {
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR" });
}

/** Formato IBAN legible: bloques de 4 chars. */
function formatIban(iban: string): string {
  const s = (iban ?? "").replace(/\s+/g, "").toUpperCase();
  return s.replace(/(.{4})/g, "$1 ").trim();
}

function statusBadge(status: PaymentBatch["status"]): JSX.Element {
  const map: Record<PaymentBatch["status"], { label: string; color: string; bg: string }> = {
    generated: { label: "Generado", color: "#555", bg: "rgba(120,120,120,0.12)" },
    partially_posted: { label: "Parcial", color: "#a60", bg: "rgba(255,180,50,0.15)" },
    posted: { label: "Contabilizado", color: "#282", bg: "rgba(80,200,120,0.15)" },
    cancelled: { label: "Anulado", color: "#a33", bg: "rgba(200,80,80,0.12)" },
  };
  const s = map[status];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: "0.85em",
        color: s.color,
        background: s.bg,
      }}
    >
      {s.label}
    </span>
  );
}
